import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { useDialog } from '@/common/dialog/DialogContext';
import { ContentBox } from '@/common/layout/MainBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { SaleStatusDefinitionsDialog } from '@/sc/entities/indirSale/dashboard/SaleStatusDefinitionsDialog';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Checkbox, ListItemText, MenuItem, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { ChbkLineVolumeAmtPaidChart } from './ChbkLineVolumeAmtPaidChart';
import { ChbkPrioritizationBubbleChart } from './ChbkPrioritizationBubbleChart';
import { ChbkProgressChart } from './ChbkProgressChart';
import { ChbkValChart } from './ChbkValChart';

export const ChbkDashboard = () => {

  const rsrc = useRsrc();

  const { openDialog } = useDialog();
  const { navState, assign } = useNavState();
  const dashboardNavState = navState['indirSaleDashboardState'] ?? {};

  const [periods, setPeriods] = useState();
  const [selectedPeriod, setSelectedPeriod] = useState();

  const [distCusts, setDistCusts] = useState();
  const [selectedDistCusts, setSelectedDistCusts] = useState([]);

  useEffect(() => {
    rsrc.data('sc', 'CHBK_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then(res => {
      setPeriods(res.periods);
      // Restore nav state
      if (dashboardNavState.selectedPeriodId) {
        const period = res.periods.find(p => p.id === dashboardNavState.selectedPeriodId);
        if (period) setSelectedPeriod(period);
      }
      // or set to first period
      else if (res.periods?.length > 0) {
        const selectedPeriod = res.periods[0];
        setSelectedPeriod(selectedPeriod);
      }

      setDistCusts(res.distCusts);
      if (dashboardNavState.selectePbmCodes) {
        const matchedDistCusts = res.distCusts.filter(distCust =>
          dashboardNavState.selecteDistCustCodes.includes(distCust.code)
        );
        if (matchedDistCusts.lenght > 0) setSelectedDistCusts(matchedDistCusts);
      } else {
        setSelectedDistCusts(res.distCusts);
      }

    });
  }, []);

  const openDefinitionsDialog = () => {
    openDialog(SaleStatusDefinitionsDialog);
  }

  // ----- NavState -----
  const saveNavState = () => {
    const newDashboardNavState = {
      selectedPeriodId: selectedPeriod?.id,
      selectedDistCustCodes: selectedDistCusts.map(d => d.code)
    };

    assign('indirSaleDashboardState', newDashboardNavState);
  };

  useEffect(() => {
    saveNavState();
  }, [selectedPeriod, selectedDistCusts]);

  if (!periods || !distCusts) return null;

  return (
    <ContentBox>
      {/* Title */}
   
      <Title
        icon={fas.faTasks}
        title="Chargeback Dashboard"
        buttons={
          [
            <TitleButton key="comparePolFormulaButton" icon={fas.faInfoCircle} toolTip="View Definitions" onClick={openDefinitionsDialog} />,
            <ExportButton />]
        }
      />
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Period */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Received Period"
          value={selectedPeriod?.id || ''}
          onChange={event => {
            const period = periods.find(p => p.id === event.target.value);
            setSelectedPeriod(period);
          }}>
          {periods?.map(period => (
            <MenuItem key={period.id} value={period.id}>
              {period.code}
            </MenuItem>
          ))}
        </TextField>

        {/* Distributors */}
        <TextField fullWidth select
          variant="outlined"
          label="Distributors"
          value={selectedDistCusts}
          onChange={event => setSelectedDistCusts(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p.code).join(', ')
          }}>
          {distCusts?.map(distCust => (
            <MenuItem key={distCust.code} value={distCust}>
              <Checkbox checked={selectedDistCusts?.some((p) => p.code === distCust.code)} />
              <ListItemText primary={distCust.name} />
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <SpacerLayout>
        <Spacer vertical>

          {/* Row 1 */}
          <Spacer weights={[2, 2, 3]}>
            <DashboardCard title="Sale Progress" sx={{ flex: 1 }}>
              <ChbkProgressChart
                selectedDistCusts={selectedDistCusts}
                selectedPeriod={selectedPeriod}
              />
            </DashboardCard>

            <DashboardCard title="Validations" sx={{ height: '100%' }}>
              <ChbkValChart
                selectedDistCusts={selectedDistCusts}
                selectedPeriod={selectedPeriod}
              />
            </DashboardCard>

            <DashboardCard title="Prioritization" sx={{ flex: 1 }}>
              <ChbkPrioritizationBubbleChart
                selectedPeriod={selectedPeriod}
                selectedDistCusts={selectedDistCusts}
              />
            </DashboardCard>
          </Spacer>

          {/* Row 2 */}
          <Spacer weights={[1]}>
            <DashboardCard title="Volume Paid">
              <ChbkLineVolumeAmtPaidChart
                selectedPeriod={selectedPeriod}
                selectedDistCusts={selectedDistCusts}
              />
            </DashboardCard>
          </Spacer>

        </Spacer>
      </SpacerLayout>

    </ContentBox>
  );
};